import { useTranslation } from "react-i18next";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  AUTOMATION_TYPES,
  type AutomationNodeData,
  type InviteActorsNodeData,
} from "@gestionalo/enterprise-graph";
import type { ProductGraph } from "@/services/platformEnterprisesService";
import type { AvailableField } from "@/lib/productGraph";

interface Props {
  data: AutomationNodeData;
  onChange: (patch: Partial<AutomationNodeData>) => void;
  availableFields: AvailableField[];
  graph: ProductGraph;
  nodeId: string;
}

export function AutomationEditor({
  data,
  onChange,
  availableFields,
  graph,
  nodeId: _nodeId,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const typeDef = AUTOMATION_TYPES.find((at) => at.type === data.automationType);

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label className="text-xs">
          {t("enterpriseProductBuilder.nodeLabelLabel")}
        </Label>
        <Input
          value={data.label ?? ""}
          onChange={(e) => onChange({ label: e.target.value })}
        />
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">
          {t("enterpriseProductBuilder.nodeDescriptionLabel")}
        </Label>
        <Textarea
          value={data.description ?? ""}
          onChange={(e) => onChange({ description: e.target.value })}
          rows={2}
        />
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">
          {t("enterpriseProductBuilder.automationType")}
        </Label>
        <Select
          value={data.automationType}
          onValueChange={(v) =>
            onChange({ automationType: v, inputMappings: {} })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {AUTOMATION_TYPES.map((at) => (
              <SelectItem key={at.type} value={at.type}>
                {at.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {typeDef && (
          <p className="text-xs text-muted-foreground">{typeDef.description}</p>
        )}
      </div>

      <div className="flex items-center justify-between gap-2">
        <div>
          <Label className="text-xs">
            {t("enterpriseProductBuilder.triggerMode")}
          </Label>
          <p className="text-xs text-muted-foreground">
            {data.triggerMode === "manual"
              ? t("enterpriseProductBuilder.triggerModeManual")
              : t("enterpriseProductBuilder.triggerModeAuto")}
          </p>
        </div>
        <Switch
          checked={data.triggerMode === "auto"}
          onCheckedChange={(checked) =>
            onChange({ triggerMode: checked ? "auto" : "manual" })
          }
        />
      </div>

      {/* Actor source (multi-actor) */}
      <div className="space-y-1.5">
        <Label className="text-xs">
          {t("enterpriseProductBuilder.automationActorSource")}
        </Label>
        <Select
          value={data.actorSource ?? "__none__"}
          onValueChange={(v) =>
            onChange({ actorSource: v === "__none__" ? undefined : v })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">
              {t("enterpriseProductBuilder.automationNoActorSource")}
            </SelectItem>
            {graph.nodes
              .filter((n) => n.type === "inviteActors")
              .map((n) => {
                const inviteData = n.data as InviteActorsNodeData;
                return (
                  <SelectItem key={n.id} value={n.id}>
                    {inviteData.label || n.id}
                  </SelectItem>
                );
              })}
          </SelectContent>
        </Select>
        {data.actorSource && (
          <p className="text-[10px] text-muted-foreground">
            {t("enterpriseProductBuilder.automationActorSourceHint")}
          </p>
        )}
      </div>

      {typeDef && typeDef.inputs.length > 0 && (
        <div className="space-y-2">
          <Label className="text-xs">
            {t("enterpriseProductBuilder.inputMappings")}
          </Label>
          {typeDef.inputs.map((inputDef) => {
            const currentMapping = data.inputMappings[inputDef.key] ?? "";
            return (
              <div key={inputDef.key} className="space-y-1">
                <Label className="text-[11px] text-muted-foreground">
                  {inputDef.label}
                  {inputDef.required && (
                    <span className="ml-1 text-destructive">*</span>
                  )}
                </Label>
                <Select
                  value={currentMapping || "__ask_at_runtime__"}
                  onValueChange={(v) => {
                    const newMappings = { ...data.inputMappings };
                    if (v === "__ask_at_runtime__") {
                      newMappings[inputDef.key] = "";
                    } else {
                      newMappings[inputDef.key] = v;
                    }
                    onChange({ inputMappings: newMappings });
                  }}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__ask_at_runtime__">
                      {t("enterpriseProductBuilder.askAtRuntime")}
                    </SelectItem>
                    {availableFields.map((f) => (
                      <SelectItem key={f.key} value={f.key}>
                        {f.label} ({f.key})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {inputDef.defaultDescription && !currentMapping && (
                  <p className="text-[10px] text-muted-foreground">
                    {inputDef.defaultDescription}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="flex items-center justify-between gap-2">
        <Label className="text-xs">
          {t("enterpriseProductBuilder.retryOnFailure")}
        </Label>
        <Switch
          checked={data.retryOnFailure ?? true}
          onCheckedChange={(checked) => onChange({ retryOnFailure: checked })}
        />
      </div>
    </div>
  );
}
